import { ActionItem, AddressDisplay, Typeahead } from '../../gs-component-form';
import {
  Content as RightRailContent,
  Footer,
  Header,
  RouterContext,
  TripleAction,
} from '../../gs-component-right-rail';
import { Field as FormikField, FormikProps } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { FormBlockColumn } from '@xo-union/tk-component-form-grid';

import { OptionalForm } from '../components/Form';
import { LocationContext } from '../components/LocationContext';
import RightRailSection from '../components/RightRailSection';
import addressComponentParser, { bookingType, LocalVendorValues } from '../helpers/addressComponentHelper';
import hasAddress from '../helpers/addressHelpers';
import { GoogleAddressValues } from '../helpers/objectifyGoogleAddressComponents';
import { populateSubEvent } from '../helpers/populateSubEvent';
import { EventInfoForm } from './EventInfoForm';
import styles from './scheduleItem.scss';
import { SoEInitialValues } from './ScheduleOfEventsForm';

interface Props {
  params?: {
    index: number;
    remove: (index: number) => void;
    newItem?: boolean;
  };
  formProps: FormikProps<SoEInitialValues>;
}

export const ScheduleItem: React.FC<Props> = ({ params, formProps }) => {
  const { push } = useContext(RouterContext);
  const { setCeremonyAddress, setReceptionAddress, location, deleteBooking } = useContext(LocationContext);

  const [loading, setLoading] = useState(false);
  const [shouldDelete, setShouldDelete] = useState(false);
  const [valuesOnEnter, setValuesOnEnter] = useState(() => populateSubEvent({}));

  const { index = 0, remove = (): void => {}, newItem = false } = params || {};
  const { values, setFieldValue } = formProps;
  const eventType = values.subEvents[index as number].type;

  useEffect(() => {
    const initValues = populateSubEvent(values.subEvents[index], location);
    setValuesOnEnter(initValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldDelete === true) {
      remove(index);
      push('main');
    }
  }, [shouldDelete, index, push, remove]);

  const goBack = (): void => push('main');

  const validateAndGoBack = (): void => {
    formProps.validateForm().then(async errors => {
      if (errors.subEvents && errors.subEvents[index]) {
        formProps.setFieldTouched(`subEvents[${index}].name`, true);
        formProps.setFieldTouched(`subEvents[${index}].time`, true);
      } else {
        goBack();
      }
    });
  };

  const deleteSubEvent = (): void => {
    setShouldDelete(true);
  };

  const cancelAndGoBack = (): void => {
    if (newItem) {
      deleteSubEvent();
    } else {
      setFieldValue(`subEvents[${index}].name`, valuesOnEnter.name);
      setFieldValue(`subEvents[${index}].time`, valuesOnEnter.time);
      setFieldValue(`subEvents[${index}].endTime`, valuesOnEnter.endTime);
      setFieldValue(`subEvents[${index}].visible`, valuesOnEnter.visible);
      setFieldValue(`subEvents[${index}].attire`, valuesOnEnter.attire);
      setFieldValue(`subEvents[${index}].notes`, valuesOnEnter.notes);
      if (valuesOnEnter.location) {
        setFieldValue(`subEvents[${index}].location.zip`, valuesOnEnter.location.zip);
        setFieldValue(`subEvents[${index}].location.city`, valuesOnEnter.location.city);
        setFieldValue(`subEvents[${index}].location.state`, valuesOnEnter.location.state);
        setFieldValue(`subEvents[${index}].location.country`, valuesOnEnter.location.country);
        setFieldValue(`subEvents[${index}].location.street1`, valuesOnEnter.location.street1);
        setFieldValue(`subEvents[${index}].location.name`, valuesOnEnter.location.name);
      }

      if (eventType === 'Ceremony' && location.CER && bookingType(location.CER.address_components) !== 'user_entry') {
        setCeremonyAddress(undefined);
      }

      if (eventType === 'Reception' && location.REC && bookingType(location.REC.address_components) !== 'user_entry') {
        setReceptionAddress(undefined);
      }
      goBack();
    }
  };

  const onChange = (addressComponentValues: GoogleAddressValues | LocalVendorValues, source: string): void => {
    const { vendor_name, vendor_profile_id, address_components, gdsAddress } = addressComponentParser(
      addressComponentValues,
      source
    );

    setFieldValue(`subEvents.${index}.location.zip`, gdsAddress.zip);
    setFieldValue(`subEvents.${index}.location.city`, gdsAddress.city);
    setFieldValue(`subEvents.${index}.location.state`, gdsAddress.state);
    setFieldValue(`subEvents.${index}.location.country`, gdsAddress.country);
    setFieldValue(`subEvents.${index}.location.street1`, gdsAddress.street1);
    setFieldValue(`subEvents.${index}.location.name`, vendor_name);

    if (eventType === 'Ceremony') {
      setCeremonyAddress({
        id: location.CER && location.CER.id,
        vendor_profile_id,
        vendor_name,
        address_components,
      });
    } else if (eventType === 'Reception') {
      setReceptionAddress({
        id: location.REC && location.REC.id,
        vendor_profile_id,
        vendor_name,
        address_components,
      });
    }
  };

  const deleteEventLocation = async (): Promise<void> => {
    setLoading(true);
    await deleteBooking(eventType);
    setLoading(false);
    setFieldValue(`subEvents[${index}].location.zip`, '');
    setFieldValue(`subEvents[${index}].location.city`, '');
    setFieldValue(`subEvents[${index}].location.state`, '');
    setFieldValue(`subEvents[${index}].location.country`, '');
    setFieldValue(`subEvents[${index}].location.street1`, '');
    setFieldValue(`subEvents[${index}].location.name`, '');
  };

  const editEventLocation = (): void =>
    push({
      screen: 'customVendor',
      params: {
        namePrefix: `subEvents[${index}].`,
        eventType,
        index,
        remove,
        newItem,
      },
    });

  const editLocationCallback = (): Function | undefined => {
    if (eventType === 'Ceremony' && location.CER && bookingType(location.CER.address_components) !== 'user_entry') {
      return undefined;
    }

    if (eventType === 'Reception' && location.REC && bookingType(location.REC.address_components) !== 'user_entry') {
      return undefined;
    }

    return editEventLocation;
  };

  return (
    <>
      <Header leftButtonText="Back" leftButtonOnClick={cancelAndGoBack} as="h1">
        {`${newItem ? 'Add' : 'Edit'} Schedule Item`}
      </Header>
      <RightRailContent>
        <RightRailSection name="Event information">
          <EventInfoForm
            namePrefix={`subEvents[${index}].`}
            setFieldValue={setFieldValue}
            showDate={false}
            autoFocusField={undefined}
          />
          {hasAddress(values.subEvents[index as number] && values.subEvents[index as number].location) ? (
            <FormBlockColumn>
              <AddressDisplay
                vendorType="Venue Name"
                deleteCallback={deleteEventLocation}
                editCallback={editLocationCallback()}
                {...values.subEvents[index as number].location}
              />
            </FormBlockColumn>
          ) : (
            <FormBlockColumn>
              <FormikField
                name={`subEvents[${index}].location.name`}
                label="Venue Name"
                onChange={onChange}
                component={Typeahead}
                onClickAddCustom={editEventLocation}
                allowCustomVendor
              />
            </FormBlockColumn>
          )}
          <OptionalForm namePrefix={`subEvents[${index}].`} />
        </RightRailSection>
        {!newItem && (
          <div className={styles.container}>
            <ActionItem type="remove" onClick={deleteSubEvent}>
              Remove This Event Schedule Item
            </ActionItem>
          </div>
        )}
      </RightRailContent>
      <Footer>
        <TripleAction primaryDisabled={loading} primaryText="Done" primaryOnClick={validateAndGoBack} />
      </Footer>
    </>
  );
};
